import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  invoicesUrl, invoiceByIdUrl, invoiceStatusUrl,
  customersUrl, productsUrl, branchesUrl, ASSETS,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtMoney, fmtDate } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, showAxiosError } from '../../utils/swal';
import { useFacility } from '../../context/FacilityContext';

const emptyItem = () => ({
  product_id: '', description: '', quantity: 1, unit_price: 0, tax_percent: 0,
});

const todayISO = () => new Date().toISOString().slice(0, 10);
const round2 = (n) => Math.round(Number(n) * 100) / 100;

const STATUS_META = {
  draft:  { label: 'Draft',  tone: 'muted' },
  issued: { label: 'Issued', tone: 'brand' },
  paid:   { label: 'Paid',   tone: 'success' },
  void:   { label: 'Void',   tone: 'danger' },
};

const Invoices = () => {
  const { facility } = useFacility();
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper = me?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');

  // Prepare modal state
  const [prepOpen, setPrepOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [billTo, setBillTo] = useState({ name: '', phone: '', email: '', address: '', company: '' });
  const [issueDate, setIssueDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState('');
  const [invBranchId, setInvBranchId] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Payment due within 14 days.');
  const [status, setStatus] = useState('issued');

  // View / print modal state
  const [viewing, setViewing] = useState(null);
  const printRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = new URL(invoicesUrl);
      if (isSuper && filterBranch !== 'all') url.searchParams.set('branch_id', filterBranch);
      if (filterStatus !== 'all') url.searchParams.set('status', filterStatus);
      const res = await axios.get(url.toString());
      if (res.data?.success) setRows(res.data.results || []);
    } catch (err) { showAxiosError(err, 'Could not load invoices'); }
    finally { setLoading(false); }
  };

  // Bootstrap
  useEffect(() => {
    load();
    axios.get(customersUrl).then((r) => setCustomers(r.data?.results || [])).catch(() => {});
    axios.get(productsUrl).then((r) => setProducts(r.data?.results || [])).catch(() => {});
    if (isSuper) axios.get(branchesUrl).then((r) => setBranches(r.data?.results || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterBranch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [
      r.invoice_no, r.bill_to_name, r.bill_to_company, r.bill_to_phone, r.branch_name,
    ].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const stats = useMemo(() => {
    const total = rows.length;
    const outstanding = rows.filter((r) => r.status === 'issued').reduce((s, r) => s + Number(r.grand_total || 0), 0);
    const paid = rows.filter((r) => r.status === 'paid').reduce((s, r) => s + Number(r.grand_total || 0), 0);
    const drafts = rows.filter((r) => r.status === 'draft').length;
    return { total, outstanding, paid, drafts };
  }, [rows]);

  // ─── Prepare modal helpers ───────────────────────────────────
  const openPrep = () => {
    setCustomerId('');
    setBillTo({ name: '', phone: '', email: '', address: '', company: '' });
    setIssueDate(todayISO());
    setDueDate('');
    setInvBranchId(isSuper ? '' : String(me?.branch_id || ''));
    setItems([emptyItem()]);
    setDiscount(0);
    setNotes('');
    setTerms('Payment due within 14 days.');
    setStatus('issued');
    setError(null);
    setPrepOpen(true);
  };

  // When the user picks a customer, prefill bill-to from that record.
  const onPickCustomer = (id) => {
    setCustomerId(id);
    if (!id) return;
    const c = customers.find((x) => Number(x.id) === Number(id));
    if (c) {
      setBillTo({
        name: c.full_name || '',
        phone: c.phone || '',
        email: c.email || '',
        address: c.address || '',
        company: c.company || '',
      });
    }
  };

  const setItem = (idx, patch) => {
    setItems((list) => list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  // Picking a product prefills description + unit_price + tax from the catalog.
  const onPickProduct = (idx, productId) => {
    const p = products.find((x) => Number(x.id) === Number(productId));
    if (!p) { setItem(idx, { product_id: '' }); return; }
    setItem(idx, {
      product_id: productId,
      description: p.name + (p.unit ? ` (${p.unit})` : ''),
      unit_price: Number(p.selling_price) || 0,
      tax_percent: Number(p.tax_percent) || 0,
    });
  };

  const addItem = () => setItems((list) => [...list, emptyItem()]);
  const removeItem = (idx) => setItems((list) => list.length === 1 ? list : list.filter((_, i) => i !== idx));

  // Totals mirror the backend formula exactly so the preview matches what gets saved.
  const totals = useMemo(() => {
    let subtotal = 0, taxTotal = 0;
    items.forEach((it) => {
      const qty = Number(it.quantity) || 0;
      const price = Number(it.unit_price) || 0;
      const gross = qty * price;
      subtotal += gross;
      taxTotal += gross * ((Number(it.tax_percent) || 0) / 100);
    });
    subtotal = round2(subtotal);
    taxTotal = round2(taxTotal);
    const disc = Math.max(0, Number(discount) || 0);
    const grand = round2(Math.max(0, subtotal + taxTotal - disc));
    return { subtotal, taxTotal, disc, grand };
  }, [items, discount]);

  const submit = async (overrideStatus) => {
    if (!billTo.name.trim()) { setError('Bill-to name is required'); return; }
    const cleanItems = items.filter((it) => (it.description || '').trim() && Number(it.quantity) > 0);
    if (cleanItems.length === 0) { setError('Add at least one item with quantity > 0'); return; }
    if (isSuper && !invBranchId) { setError('Pick which branch is issuing this invoice'); return; }
    setError(null); setSaving(true);
    const finalStatus = overrideStatus || status;
    try {
      const payload = {
        branch_id: invBranchId || undefined,
        customer_id: customerId || null,
        bill_to_name: billTo.name.trim(),
        bill_to_phone: billTo.phone || null,
        bill_to_email: billTo.email || null,
        bill_to_address: billTo.address || null,
        bill_to_company: billTo.company || null,
        issue_date: issueDate,
        due_date: dueDate || null,
        currency: 'GHS',
        items: cleanItems,
        discount: totals.disc,
        notes: notes || null,
        terms: terms || null,
        status: finalStatus,
      };
      const res = await axios.post(invoicesUrl, payload);
      toastSuccess(`Invoice ${res.data?.invoice_no || 'created'}`);
      setPrepOpen(false);
      await load();
      if (res.data?.id) openView(res.data.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  // ─── View / print modal ────────────────────────────────────
  const openView = async (id) => {
    setViewing({ loading: true });
    try {
      const res = await axios.get(invoiceByIdUrl(id));
      if (res.data?.success) setViewing({ invoice: res.data.invoice, items: res.data.items });
    } catch (err) {
      showAxiosError(err, 'Could not load invoice');
      setViewing(null);
    }
  };

  const printInvoice = () => {
    if (!printRef.current) return;
    // Open a clean print window with just the invoice markup — sidebars, tabs,
    // toolbars all stay out of the printed page.
    const html = printRef.current.innerHTML;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${viewing?.invoice?.invoice_no || 'Invoice'}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Inter, -apple-system, Segoe UI, sans-serif; color: #0f172a; margin: 0; padding: 40px; }
        h1, h2, h3 { margin: 0 0 6px; }
        .inv-print-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; }
        .brand-logo { max-height: 60px; margin-bottom: 8px; }
        .brand-name { font-size: 22px; font-weight: 800; letter-spacing: -0.01em; }
        .brand-sub  { color: #64748b; font-size: 12.5px; }
        .inv-meta   { text-align: right; font-size: 12.5px; color: #334155; }
        .inv-meta strong { display: block; color: #0f172a; font-size: 22px; }
        .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
        .party-block { padding: 16px; border: 1px solid #e2e8f0; border-radius: 10px; }
        .party-block .lbl { text-transform: uppercase; font-size: 10.5px; letter-spacing: 0.06em; color: #64748b; margin-bottom: 6px; }
        .party-block .nm  { font-weight: 700; font-size: 14px; margin-bottom: 2px; }
        .party-block .li  { font-size: 12.5px; color: #334155; }
        table.inv-tbl { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        table.inv-tbl thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 10px 12px; border-bottom: 2px solid #0f172a; }
        table.inv-tbl thead th.right { text-align: right; }
        table.inv-tbl tbody td { padding: 12px; border-bottom: 1px solid #e2e8f0; vertical-align: top; font-size: 13px; }
        table.inv-tbl tbody td.right { text-align: right; font-variant-numeric: tabular-nums; }
        .totals { display: flex; justify-content: flex-end; }
        .totals-tbl { min-width: 280px; }
        .totals-tbl .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #334155; }
        .totals-tbl .row.grand { border-top: 2px solid #0f172a; padding-top: 12px; margin-top: 8px; font-size: 15px; color: #0f172a; font-weight: 800; }
        .footnotes { margin-top: 28px; font-size: 12px; color: #475569; }
        .footnotes h4 { margin: 8px 0 4px; color: #0f172a; font-size: 12.5px; text-transform: uppercase; letter-spacing: 0.05em; }
        @page { margin: 20mm; }
      </style>
      </head><body>${html}<script>window.onload = () => { setTimeout(() => window.print(), 250); };</script></body></html>`);
    w.document.close();
  };

  const setStatusFor = async (row, next) => {
    try {
      await axios.patch(invoiceStatusUrl(row.id), { status: next });
      toastSuccess(`Marked ${next}`);
      await load();
      if (viewing?.invoice?.id === row.id) openView(row.id);
    } catch (err) { showAxiosError(err); }
  };

  const removeInvoice = async (row) => {
    const isDraft = row.status === 'draft';
    const ok = await swalDangerConfirm(
      isDraft ? 'Delete this draft?' : 'Void this invoice?',
      isDraft ? 'Drafts are removed permanently.' : 'The invoice is kept for records but marked void.',
      { confirmButtonText: isDraft ? 'Delete' : 'Void' }
    );
    if (!ok) return;
    try {
      await axios.delete(invoiceByIdUrl(row.id));
      toastSuccess(isDraft ? 'Draft deleted' : 'Invoice voided');
      await load();
    } catch (err) { showAxiosError(err); }
  };

  return (
    <>
      <div className="prod-stats">
        <div className="prod-stat tone-brand">
          <div className="ic">🧾</div>
          <div className="body">
            <div className="lbl">Invoices</div>
            <div className="val">{stats.total}</div>
            <div className="hint">{stats.drafts} in draft</div>
          </div>
        </div>
        <div className="prod-stat tone-warn">
          <div className="ic">⏳</div>
          <div className="body">
            <div className="lbl">Outstanding</div>
            <div className="val">{fmtMoney(stats.outstanding)}</div>
            <div className="hint">Issued but not yet paid</div>
          </div>
        </div>
        <div className="prod-stat tone-success">
          <div className="ic">✅</div>
          <div className="body">
            <div className="lbl">Collected</div>
            <div className="val">{fmtMoney(stats.paid)}</div>
            <div className="hint">Total marked paid</div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>Invoices</h2>
            <p className="muted-sm" style={{ margin: '2px 0 0' }}>
              Prepare a bill for a customer. This is a document — it does not affect sales or stock.
            </p>
          </div>
          <div className="panel-toolbar">
            {isSuper && (
              <select className="f-select" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)} style={{ width: 160 }}>
                <option value="all">All branches</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <select className="f-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: 140 }}>
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
            </select>
            <div className="prod-search">
              <span className="ic">🔍</span>
              <input className="f-input" placeholder="Search invoice #, customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button className="btn-primary" onClick={openPrep}>+ Prepare invoice</button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading invoices…</p>
        ) : filtered.length === 0 ? (
          <div className="cust-empty">
            <div className="ic">🧾</div>
            <h3>{rows.length === 0 ? 'No invoices yet' : 'No invoices match your filter'}</h3>
            <p>
              {rows.length === 0
                ? 'Prepare your first invoice — the number is generated automatically. You can save it as draft or issue it right away.'
                : 'Try clearing the search or switching the status filter.'}
            </p>
            {rows.length === 0 && <button className="btn-primary" onClick={openPrep}>+ Prepare your first invoice</button>}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Bill to</th>
                  {isSuper && <th>Branch</th>}
                  <th>Issued</th>
                  <th>Due</th>
                  <th className="right">Total</th>
                  <th>Status</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.invoice_no}</strong></td>
                    <td>
                      <div>{r.bill_to_name}</div>
                      {r.bill_to_company && <div className="muted-sm">🏢 {r.bill_to_company}</div>}
                    </td>
                    {isSuper && <td><span className="branch-tag">📍 {r.branch_name}</span></td>}
                    <td className="muted-sm">{fmtDate(r.issue_date)}</td>
                    <td className="muted-sm">{r.due_date ? fmtDate(r.due_date) : '—'}</td>
                    <td className="right"><strong>{fmtMoney(r.grand_total)}</strong></td>
                    <td>
                      <span className={`inv-status-badge ${r.status}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="right">
                      <div className="row-icons">
                        <button className="icon-btn brand" onClick={() => openView(r.id)} title="View / Print">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 2h6l4 4v12H6z"/><path d="M12 2v4h4"/>
                          </svg>
                        </button>
                        {r.status === 'issued' && (
                          <button className="icon-btn success" onClick={() => setStatusFor(r, 'paid')} title="Mark paid">₵</button>
                        )}
                        {r.status !== 'void' && (
                          <button className="icon-btn danger" onClick={() => removeInvoice(r)} title={r.status === 'draft' ? 'Delete draft' : 'Void'}>
                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── PREPARE INVOICE MODAL ─── */}
      <Modal
        title="Prepare invoice"
        open={prepOpen} onClose={() => setPrepOpen(false)} width={880}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setPrepOpen(false)}>Cancel</button>
            <button className="btn-ghost" onClick={() => submit('draft')} disabled={saving}>Save as draft</button>
            <button className="btn-primary" onClick={() => submit('issued')} disabled={saving}>
              {saving ? 'Saving…' : 'Issue invoice'}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Customer <span className="muted-sm">(optional — search or leave blank for one-off)</span></label>
            <select className="f-select" value={customerId} onChange={(e) => onPickCustomer(e.target.value)}>
              <option value="">— One-off customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name}{c.company ? ` · ${c.company}` : ''}{c.phone ? ` · ${c.phone}` : ''}
                </option>
              ))}
            </select>
          </div>
          {isSuper && (
            <div className="f-row">
              <label className="f-label">Branch issuing *</label>
              <select className="f-select" value={invBranchId} onChange={(e) => setInvBranchId(e.target.value)}>
                <option value="">— Pick a branch —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <h4 className="prod-section-head">Bill to</h4>
        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Name *</label>
            <input className="f-input" value={billTo.name} onChange={(e) => setBillTo({ ...billTo, name: e.target.value })} placeholder="e.g. Kwame Boateng" />
          </div>
          <div className="f-row">
            <label className="f-label">Company</label>
            <input className="f-input" value={billTo.company} onChange={(e) => setBillTo({ ...billTo, company: e.target.value })} placeholder="e.g. Seraphim Ltd." />
          </div>
          <div className="f-row">
            <label className="f-label">Phone</label>
            <input className="f-input" value={billTo.phone} onChange={(e) => setBillTo({ ...billTo, phone: e.target.value })} placeholder="0244 000 000" />
          </div>
          <div className="f-row">
            <label className="f-label">Email</label>
            <input className="f-input" type="email" value={billTo.email} onChange={(e) => setBillTo({ ...billTo, email: e.target.value })} />
          </div>
          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Address</label>
            <input className="f-input" value={billTo.address} onChange={(e) => setBillTo({ ...billTo, address: e.target.value })} placeholder="e.g. East Legon, Accra" />
          </div>
        </div>

        <h4 className="prod-section-head">Dates</h4>
        <div className="f-grid f-grid-2">
          <div className="f-row">
            <label className="f-label">Issue date *</label>
            <input className="f-input" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>
          <div className="f-row">
            <label className="f-label">Due date <span className="muted-sm">(optional)</span></label>
            <input className="f-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        </div>

        <h4 className="prod-section-head">Line items</h4>
        <div className="inv-items">
          <table className="inv-items-tbl">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Product <span className="muted-sm">(or free text)</span></th>
                <th>Description</th>
                <th style={{ width: 90 }} className="right">Qty</th>
                <th style={{ width: 120 }} className="right">Unit price</th>
                <th style={{ width: 80 }} className="right">Tax %</th>
                <th style={{ width: 130 }} className="right">Total</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                const line = gross * (1 + (Number(it.tax_percent) || 0) / 100);
                return (
                  <tr key={idx}>
                    <td>
                      <select className="f-select" value={it.product_id || ''} onChange={(e) => onPickProduct(idx, e.target.value)}>
                        <option value="">— Free item —</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>{p.name} · {p.sku}</option>
                        ))}
                      </select>
                    </td>
                    <td><input className="f-input" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} placeholder="Item description" /></td>
                    <td><input className="f-input right" type="number" step="0.001" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} /></td>
                    <td><input className="f-input right" type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} /></td>
                    <td><input className="f-input right" type="number" step="0.01" value={it.tax_percent} onChange={(e) => setItem(idx, { tax_percent: e.target.value })} /></td>
                    <td className="right" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(round2(line))}</td>
                    <td>
                      <button className="icon-btn danger" onClick={() => removeItem(idx)} disabled={items.length === 1} title="Remove row">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button className="btn-ghost" onClick={addItem} style={{ marginTop: 10 }}>+ Add another line</button>
        </div>

        <div className="inv-summary">
          <div className="left">
            <div className="f-row">
              <label className="f-label">Discount (GHS)</label>
              <input className="f-input" type="number" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            </div>
            <div className="f-row">
              <label className="f-label">Notes</label>
              <textarea className="f-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the customer should see under the items…" />
            </div>
            <div className="f-row">
              <label className="f-label">Terms &amp; conditions</label>
              <textarea className="f-textarea" rows={2} value={terms} onChange={(e) => setTerms(e.target.value)} />
            </div>
          </div>
          <div className="right">
            <div className="totals-card">
              <div className="row"><span>Subtotal</span><span>{fmtMoney(totals.subtotal)}</span></div>
              <div className="row"><span>Tax</span><span>{fmtMoney(totals.taxTotal)}</span></div>
              {totals.disc > 0 && <div className="row" style={{ color: 'var(--danger)' }}><span>Discount</span><span>−{fmtMoney(totals.disc)}</span></div>}
              <div className="row grand"><span>Grand total</span><span>{fmtMoney(totals.grand)}</span></div>
            </div>
          </div>
        </div>
      </Modal>

      {/* ─── VIEW / PRINT MODAL ─── */}
      <Modal
        title={viewing?.invoice ? `Invoice ${viewing.invoice.invoice_no}` : 'Invoice'}
        open={!!viewing} onClose={() => setViewing(null)} width={860}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setViewing(null)}>Close</button>
            {viewing?.invoice && (
              <button className="btn-primary" onClick={printInvoice}>Print / Download</button>
            )}
          </>
        }
      >
        {viewing?.loading || !viewing?.invoice ? (
          <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading invoice…</p>
        ) : (
          <div ref={printRef}>
            <div className="inv-print-head">
              <div>
                {facility?.logo && <img src={`${ASSETS}${facility.logo}`} className="brand-logo" alt={facility?.name} />}
                <div className="brand-name">{facility?.name || 'POS Ghana'}</div>
                <div className="brand-sub">{viewing.invoice.branch_name}</div>
              </div>
              <div className="inv-meta">
                <div>INVOICE</div>
                <strong>{viewing.invoice.invoice_no}</strong>
                <div>Issued: {fmtDate(viewing.invoice.issue_date)}</div>
                {viewing.invoice.due_date && <div>Due: {fmtDate(viewing.invoice.due_date)}</div>}
                <div style={{ marginTop: 6 }}>Status: <strong>{STATUS_META[viewing.invoice.status]?.label || viewing.invoice.status}</strong></div>
              </div>
            </div>

            <div className="parties">
              <div className="party-block">
                <div className="lbl">From</div>
                <div className="nm">{facility?.name || 'POS Ghana'}</div>
                <div className="li">{viewing.invoice.branch_name}</div>
              </div>
              <div className="party-block">
                <div className="lbl">Bill to</div>
                <div className="nm">{viewing.invoice.bill_to_name}</div>
                {viewing.invoice.bill_to_company && <div className="li">🏢 {viewing.invoice.bill_to_company}</div>}
                {viewing.invoice.bill_to_phone && <div className="li">☎ {viewing.invoice.bill_to_phone}</div>}
                {viewing.invoice.bill_to_email && <div className="li">✉ {viewing.invoice.bill_to_email}</div>}
                {viewing.invoice.bill_to_address && <div className="li">📍 {viewing.invoice.bill_to_address}</div>}
              </div>
            </div>

            <table className="inv-tbl">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="right">Qty</th>
                  <th className="right">Unit price</th>
                  <th className="right">Tax %</th>
                  <th className="right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {viewing.items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.description}{it.product_sku && <div className="muted-sm">{it.product_sku}</div>}</td>
                    <td className="right">{Number(it.quantity)}</td>
                    <td className="right">{fmtMoney(it.unit_price)}</td>
                    <td className="right">{Number(it.tax_percent)}%</td>
                    <td className="right">{fmtMoney(it.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="totals">
              <div className="totals-tbl">
                <div className="row"><span>Subtotal</span><span>{fmtMoney(viewing.invoice.subtotal)}</span></div>
                <div className="row"><span>Tax</span><span>{fmtMoney(viewing.invoice.tax_total)}</span></div>
                {Number(viewing.invoice.discount) > 0 && (
                  <div className="row" style={{ color: '#b91c1c' }}><span>Discount</span><span>−{fmtMoney(viewing.invoice.discount)}</span></div>
                )}
                <div className="row grand"><span>Grand total</span><span>{fmtMoney(viewing.invoice.grand_total)}</span></div>
              </div>
            </div>

            {(viewing.invoice.notes || viewing.invoice.terms) && (
              <div className="footnotes">
                {viewing.invoice.notes && (<><h4>Notes</h4><div>{viewing.invoice.notes}</div></>)}
                {viewing.invoice.terms && (<><h4 style={{ marginTop: 12 }}>Terms</h4><div>{viewing.invoice.terms}</div></>)}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default Invoices;
